import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { EmailOnboard } from "../../components/Onboarding/EmailOnboard";
import { Login } from "../../components/Onboarding/Login";
import { SignUp } from "../../components/Onboarding/SignUp";
import {
  ContactPhone,
  OnboardingNotice,
  OrgName,
  PrimaryMarket,
} from "../../components/Onboarding/Attraction";
import { OnboardingContext } from "../../context";
import AuthService from "../../service/authService";
import { COUNTRY, UserOnboardingInfo } from "../../types";
import {
  ATTRACTION_ONBOARDING_STEPS,
  ATTRACTION_PRIMARY_MARKETS,
  PROJECT_TYPES,
  ROUTES,
} from "../../constant/common.constant";
import { getLanguage } from "../../utils/LocaleUtil";
import { useTokenInfo } from "../../utils/ReactHooks";
import { useUserInfoFetcher } from "../../utils/UserHooks";
import { OnboardingTemplate } from "./OnboardingTemplate";
import { trackAccountCreated } from "../../utils/analytics.utils";
import { useTeam } from "../../context/Team";
import {
  createCheckEmailHandler,
  getSafeAttractionRedirectUrl,
  hasActiveStudioSession,
  createSignInHandler,
  validatePasswordStep,
} from "./signIn";

export const AttractionOnboardingContainer = () => {
  const { token: inviteToken } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const campaign = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return query.get("campaign");
  }, [location.search]);
  const redirectUrl = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return getSafeAttractionRedirectUrl(query.get("redirect_url"));
  }, [location.search]);
  const getRedirectUrl = useCallback(() => {
    const query = new URLSearchParams(window.location.search);
    return getSafeAttractionRedirectUrl(query.get("redirect_url"));
  }, []);

  const [currentStep, setCurrentStep] = useState<ATTRACTION_ONBOARDING_STEPS>(
    ATTRACTION_ONBOARDING_STEPS.EMAIL
  );
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [userId, setUserId] = useState("");
  const [selectedMarket, setSelectedMarket] = useState<
    ATTRACTION_PRIMARY_MARKETS | undefined
  >(undefined);

  const { refetchTeam } = useTeam();
  const { initUserInfo } = useUserInfoFetcher();

  const [onboardingInfo, setOnboardingInfo] = useState<UserOnboardingInfo>({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    optNewsletter: false,
    language: getLanguage(),
    country: COUNTRY.CA,
    forWho: undefined,
    contactPhone: "",
    projectSize: "",
    teamName: "",
    organizationName: "",
    projectName: "",
    campaign: campaign || "",
  });

  const onboardingInfoRef = useRef(onboardingInfo);
  useEffect(() => {
    onboardingInfoRef.current = onboardingInfo;
  }, [onboardingInfo]);

  useEffect(() => {
    setOnboardingInfo((infos) => ({
      ...infos,
      campaign: campaign || "",
      language: getLanguage(),
    }));
  }, [campaign]);

  const [errorMsg, setErrorMsg] = useState("");
  const tokenInfo = useTokenInfo(inviteToken);

  useEffect(() => {
    if (!redirectUrl || inviteToken) {
      return;
    }

    let isMounted = true;

    hasActiveStudioSession()
      .then((hasSession) => {
        if (isMounted && hasSession) {
          window.location.assign(redirectUrl);
        }
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      isMounted = false;
    };
  }, [inviteToken, redirectUrl]);

  const onSignInClick = createSignInHandler({
    inviteToken,
    setErrorMsg,
    initUserInfo,
    refetchTeam,
    navigate,
    redirectUrl,
    getRedirectUrl,
    onNotDistributor: (userId: string) => {
      setUserId(userId);
      setCurrentStep(ATTRACTION_ONBOARDING_STEPS.NOT_DISTRIBUTOR);
    },
    onDistributorDenied: () => {
      setCurrentStep(ATTRACTION_ONBOARDING_STEPS.DISTRIBUTOR_DENIED);
    },
  });

  const onCheckEmailNextStep = useCallback(
    createCheckEmailHandler({
      setErrorMsg,
      setOnboardingInfo,
      setIsAuthorizedDistributor: setIsWhitelisted,
      onGoToLogin: () => setCurrentStep(ATTRACTION_ONBOARDING_STEPS.LOGIN),
      onGoToSignup: () => setCurrentStep(ATTRACTION_ONBOARDING_STEPS.SIGNUP),
    }),
    []
  );

  const onBackToEmailStep = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      setCurrentStep(ATTRACTION_ONBOARDING_STEPS.EMAIL);
      setIsWhitelisted(false);
    },
    []
  );

  const onSignUpGetStarted = useCallback(
    async (values: UserOnboardingInfo) => {
      setErrorMsg("");
      const formValues = { ...values };
      formValues.eventDate = undefined;
      formValues.inviteToken = inviteToken || undefined;
      formValues.language = getLanguage();

      const res = await AuthService.signUp(formValues).catch((error) => {
        return error.response;
      });

      if (!res?.data) {
        setErrorMsg(t("sign_up.network_error"));
        return;
      }

      const { data, error } = res.data;

      if (res.status < 200 || res.status >= 300) {
        const message = error?.message;
        if (message === "username_already_exists") {
          setErrorMsg(t("sign_up." + message, { email: formValues.email }));
          return;
        }
        if (message === "attraction_awaiting_approval") {
          setCurrentStep(
            ATTRACTION_ONBOARDING_STEPS.NEW_ACCOUNT_AWAITING_APPROVAL
          );
          return;
        }
        setErrorMsg(t("sign_up." + message));
        return;
      }

      const { newUser, newProject, joinedTeam, newTeam } = data;

      AuthService.setLocalUser(newUser.currency, newUser.country);
      initUserInfo();

      trackAccountCreated({
        firstName: formValues.firstName ?? "",
        lastName: formValues.lastName ?? "",
        email: formValues.email ?? "",
        invited: !!inviteToken,
        forWho: formValues.forWho,
        currency: newUser.currency,
        country: formValues.country ?? COUNTRY.CA,
        contactPhone: formValues.contactPhone,
      });

      if (!inviteToken) {
        navigate(
          generatePath(ROUTES.PROJECT, {
            projectId: newProject.id,
            teamId: newTeam.id,
          })
        );
        return;
      }

      navigate(generatePath(ROUTES.TEAM, { teamId: joinedTeam.id }));
    },
    [setErrorMsg, initUserInfo, t, navigate, inviteToken]
  );

  const onCheckUserInfoNextStep = useCallback(
    async (values: UserOnboardingInfo) => {
      setErrorMsg("");
      const valid = await validatePasswordStep(
        values.password ?? "",
        t,
        setErrorMsg
      );
      if (!valid) return;
      setOnboardingInfo(values);

      if (isWhitelisted) {
        await onSignUpGetStarted({
          ...values,
        });
        return;
      }

      setCurrentStep(ATTRACTION_ONBOARDING_STEPS.PRIMARY_MARKET);
    },
    [isWhitelisted, onSignUpGetStarted, t]
  );

  const onPrimaryMarketNextStep = useCallback(
    (market: ATTRACTION_PRIMARY_MARKETS) => {
      setSelectedMarket(market);
      if (inviteToken) {
        // Joining a team: block only souvenir (recreo), all other markets proceed to signup
        if (market === ATTRACTION_PRIMARY_MARKETS.SOUVENIR) {
          setCurrentStep(ATTRACTION_ONBOARDING_STEPS.MARKET_UNAVAILABLE);
        } else if (market === ATTRACTION_PRIMARY_MARKETS.PROMO) {
          setCurrentStep(ATTRACTION_ONBOARDING_STEPS.ORG_NAME);
        } else {
          onSignUpGetStarted({ ...onboardingInfoRef.current });
        }
      } else {
        // Creating an account: block unless promo
        setCurrentStep(
          market === ATTRACTION_PRIMARY_MARKETS.PROMO
            ? ATTRACTION_ONBOARDING_STEPS.ORG_NAME
            : ATTRACTION_ONBOARDING_STEPS.MARKET_UNAVAILABLE
        );
      }
    },
    [inviteToken, onSignUpGetStarted]
  );

  const onOrgNameNextStep = useCallback((orgName: string) => {
    setOnboardingInfo((prev) => ({ ...prev, organizationName: orgName }));
    setCurrentStep(ATTRACTION_ONBOARDING_STEPS.CONTACT_PHONE);
  }, []);

  const onContactPhoneNextStep = useCallback(
    async (contactPhone: string) => {
      await onSignUpGetStarted({
        ...onboardingInfoRef.current,
        contactPhone,
        forWho: PROJECT_TYPES.DISTRIBUTOR,
      });
    },
    [onSignUpGetStarted, t]
  );

  const onBackFromPrimaryMarket = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      setCurrentStep(ATTRACTION_ONBOARDING_STEPS.SIGNUP);
    },
    []
  );

  const onBackFromOrgName = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      setCurrentStep(ATTRACTION_ONBOARDING_STEPS.PRIMARY_MARKET);
    },
    []
  );

  const onBackFromContactPhone = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      setCurrentStep(ATTRACTION_ONBOARDING_STEPS.ORG_NAME);
    },
    []
  );

  const onBackFromOnboardingNotice = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      setCurrentStep(ATTRACTION_ONBOARDING_STEPS.PRIMARY_MARKET);
    },
    []
  );

  return (
    <OnboardingContext.Provider value={{ errorMsg, tokenInfo }}>
      <OnboardingTemplate
        hideMarquee={
          currentStep === ATTRACTION_ONBOARDING_STEPS.MARKET_UNAVAILABLE
        }
      >
        {currentStep === ATTRACTION_ONBOARDING_STEPS.EMAIL && (
          <EmailOnboard
            onNextStep={onCheckEmailNextStep}
            email={onboardingInfo.email}
          />
        )}
        {currentStep === ATTRACTION_ONBOARDING_STEPS.LOGIN &&
          !!onboardingInfo.email && (
            <Login
              onNextStep={onSignInClick}
              onPreviousStep={onBackToEmailStep}
              email={onboardingInfo.email}
            />
          )}
        {currentStep === ATTRACTION_ONBOARDING_STEPS.SIGNUP && (
          <SignUp
            onNextStep={onCheckUserInfoNextStep}
            onPreviousStep={onBackToEmailStep}
            userInfo={onboardingInfo}
            setUserInfo={setOnboardingInfo}
            hideNewsletter
            submitLabel={isWhitelisted ? t("onboard.lets_jam") : undefined}
          />
        )}
        {currentStep === ATTRACTION_ONBOARDING_STEPS.PRIMARY_MARKET && (
          <PrimaryMarket
            onNextStep={onPrimaryMarketNextStep}
            onPreviousStep={onBackFromPrimaryMarket}
            selectedMarket={selectedMarket}
          />
        )}
        {currentStep === ATTRACTION_ONBOARDING_STEPS.ORG_NAME && (
          <OrgName
            onNextStep={onOrgNameNextStep}
            onPreviousStep={onBackFromOrgName}
            initialValue={onboardingInfo.organizationName}
          />
        )}
        {currentStep === ATTRACTION_ONBOARDING_STEPS.CONTACT_PHONE && (
          <ContactPhone
            onNextStep={onContactPhoneNextStep}
            onPreviousStep={onBackFromContactPhone}
            initialValue={onboardingInfo.contactPhone}
          />
        )}
        {currentStep === ATTRACTION_ONBOARDING_STEPS.MARKET_UNAVAILABLE &&
          selectedMarket !== undefined &&
          selectedMarket !== ATTRACTION_PRIMARY_MARKETS.PROMO && (
            <OnboardingNotice
              reason={
                selectedMarket === ATTRACTION_PRIMARY_MARKETS.SOUVENIR
                  ? "souvenir"
                  : "other"
              }
              onPreviousStep={onBackFromOnboardingNotice}
            />
          )}
        {currentStep ===
          ATTRACTION_ONBOARDING_STEPS.EXISTING_ACCOUNT_AWAITING_APPROVAL && (
          <OnboardingNotice reason="existing_account_awaiting_approval" />
        )}
        {currentStep === ATTRACTION_ONBOARDING_STEPS.NOT_DISTRIBUTOR && (
          <OnboardingNotice
            reason="not_distributor"
            action={{
              label: t(
                "attraction.onboard.onboarding_notice.request_distributor_access_cta"
              ),
              onClick: async () => {
                await AuthService.requestDistributorAccess(userId);
                setCurrentStep(
                  ATTRACTION_ONBOARDING_STEPS.EXISTING_ACCOUNT_AWAITING_APPROVAL
                );
              },
            }}
          />
        )}
        {currentStep ===
          ATTRACTION_ONBOARDING_STEPS.NEW_ACCOUNT_AWAITING_APPROVAL && (
          <OnboardingNotice reason="new_account_awaiting_approval" />
        )}
        {currentStep === ATTRACTION_ONBOARDING_STEPS.DISTRIBUTOR_DENIED && (
          <OnboardingNotice reason="distributor_denied" />
        )}
      </OnboardingTemplate>
    </OnboardingContext.Provider>
  );
};
